import { FormikContext } from 'formik';
import { useContext, useMemo } from 'react';
import { PiWarning } from 'react-icons/pi';
import { useLocation } from 'react-router-dom';
import {
  flattenTabErrors,
  flattenTabMessages,
  TabErrorValue,
} from 'utils/global/validation.utils';
import { useTabsNavigationContext } from './TabsNavigationContext';
import './TabContentErrorBanner.scss';

interface ITabContentAlertBannerProps {
  variant: 'error' | 'warning';
  messages: string[];
}

const TabContentAlertBanner = ({
  variant,
  messages,
}: ITabContentAlertBannerProps) => (
  <div
    className={`tab-content-alert-banner tab-content-alert-banner--${variant}`}
    role={variant === 'error' ? 'alert' : 'status'}>
    <PiWarning className='tab-content-alert-banner__icon' />
    <div className='tab-content-alert-banner__messages'>
      {messages.map(message => (
        <p key={message} className='tab-content-alert-banner__message'>
          {message}
        </p>
      ))}
    </div>
  </div>
);

const TabContentErrorBanner = () => {
  const { pathname } = useLocation();
  const tabsNavigationContext = useTabsNavigationContext();
  const formik = useContext(FormikContext);
  const isDisplayMode = !!(
    formik?.status?.displayMode ?? tabsNavigationContext?.displayMode
  );
  const shouldShowValidationErrors =
    isDisplayMode || (formik?.submitCount ?? 0) > 0;

  const activeTab = useMemo(
    () =>
      tabsNavigationContext?.tabs.find(tab =>
        pathname.split('/').includes(tab.name),
      ),
    [pathname, tabsNavigationContext?.tabs],
  );

  const errorMessages = useMemo(() => {
    if (!shouldShowValidationErrors || !activeTab?.error) {
      return [];
    }

    return Array.from(
      new Set(flattenTabErrors(activeTab.error as TabErrorValue)),
    );
  }, [activeTab?.error, shouldShowValidationErrors]);

  const warningMessages = useMemo(() => {
    if (!activeTab?.warning) {
      return [];
    }

    return Array.from(new Set(flattenTabMessages(activeTab.warning)));
  }, [activeTab?.warning]);

  if (!errorMessages.length && !warningMessages.length) {
    return null;
  }

  return (
    <>
      {!!warningMessages.length && (
        <TabContentAlertBanner variant='warning' messages={warningMessages} />
      )}
      {!!errorMessages.length && (
        <TabContentAlertBanner variant='error' messages={errorMessages} />
      )}
    </>
  );
};

export default TabContentErrorBanner;
